import { HttpError, useCreate, useOne } from "@refinedev/core";
import Button from "components/button";
import { FullScreenLoading } from "components/fullScreenLoading";
import Select from "components/select";
import { IEventIdsItem } from "interfaces";
import { useState, useCallback, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { paginate } from "utills/helpers";
import { DEFAULT_PAGE_SIZE } from "utills/constants";
import Pagination from "components/pagination";
import { Link, useNavigate } from "react-router-dom";
import { get, isArray, isEmpty } from "lodash";
import toast from "react-hot-toast";
import { ConfirmModal } from "components/confirmModal";
import { DownloadIcon } from "@heroicons/react/outline";
import { MassPasscodeCard } from "./card";
import { MassPasscodeTable } from "./table";

const IMPORT_ACTIONS = [
  {label: "Create", value: "create"},
  {label: "Update", value: "update"}
];

const INITIAL_MODAL_CONFIG = {
  open: false,
  submit: () => {}
}

export const ImportPasscode = () => {
  const [file, setFile] = useState<any>(null);
  const [data, setData] = useState<{[x:string]:any}[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [error, setError] = useState('');

  const [confirmModalConfig, setConfirmModalConfig] = useState(INITIAL_MODAL_CONFIG);

  const [eventId, setEventId] = useState("");
  const [action, setAction] = useState("create");

  const [pageNumber, setPageNumber] = useState(1);
  const [exportId, setExportId] = useState<string | null>(null);

  const { mutate, isLoading: isLoading } = useCreate();
  const navigate = useNavigate();
  
  const { data: eventsData, isFetching } = useOne<IEventIdsItem[], HttpError>({
    resource: "content/dropdown/events?uncheck=true",
    id: "",
    meta: {
      resource: "data"
    }
  });
  
  const { refetch: checkStatus } = useOne({
    resource: "private-flow/event-passcodes/import/result",
    id: exportId ?? "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: false,
    } as any,
    errorNotification: (data, values, resource) => {
      setExportId(null);
      return {
        message: `Export Error`,
        description: "Export Error",
        type: "error",
      }
    },
  });

  useEffect(() => {
    let intervalId: any;

    if (exportId) {
      intervalId = setInterval(() => {
        checkStatus().then((res) => {
          const isProcessing = get(res, "data.data.status") === "processing";
          const isError = get(res, "data.data.status") === "error";
          const isSuccess = get(res, "data.data.status") === "success";

          if (!isProcessing) {
            setExportId(null);
            const fileLink = get(res, "data.data.file_link", "") || "#";
            if (isError) {
              setConfirmModalConfig({
                open: true,
                submit: () => {
                  if (fileLink) {
                    window.location.href = fileLink;
                    _clearStateAfterDownload();
                  }
                },
              })
            } else if (isSuccess) {
              toast.success("Imported successfully");
              navigate("/events/passcodes");
            }
          }
        });
      }, 2000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportId, checkStatus]);
  
  const handleFileSelect = useCallback(async () => {
    try {
      setError('');
      
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.xlsx,.xls,.csv';
      
      input.onchange = async (event: any) => {
        const file = event.target.files[0];
        if (!file) {
          return;
        }
        setFile(file);
        try {
          const arrayBuffer = await file.arrayBuffer();
          const workbook = XLSX.read(arrayBuffer, { type: 'array' });
          
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          if (jsonData.length > 0) {
            const headerRow = jsonData[0] as string[];
            setHeaders(headerRow);
            
            const rows = jsonData.slice(1).map((row: any, index: number) => {
              const rowData: any = { id: index };
              headerRow.forEach((header: string, colIndex: number) => {
                rowData[header] = row[colIndex] || '';
              });
              return rowData;
            });
            
            setData(rows);
            setPageNumber(1);
          }
        } catch (err: any) {
          setError('Error while reading file: ' + err.message);
        }
      };
                        
      input.click();
    } catch (err: any) {
      setError('Error: ' + err.message);
    }
  }, []);

  const _checkStatus = (id: string) => {
    setExportId(id);
  }

  const _submit = async () => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("action", action);
    formData.append("event_id", eventId);

    mutate(
      {
        resource: "private-flow/event-passcodes/import",
        meta: {
          ignoreResourceWrapper: true,
        },
        values: formData,
      },
      {
        onSuccess: (res) => {
          const isProcessing= get(res, "data.data.status") === "processing";
          const isError = get(res, "data.data.status") === "error";
          const isSuccess = get(res, "data.data.status") === "success";

          if (isProcessing) {
            const exportId = get(res, "data.data.task_id", "");
            _checkStatus(exportId)
          } else {
            const fileLink = get(data, "data.data.file_link", "") || "#";
            if (isError) {
              setConfirmModalConfig({
                open: true,
                submit: () => window.location.href = fileLink,
              })
            } else if (isSuccess) {
              toast.success("Imported successfully");
              navigate("/events/passcodes");
            }
          }
        },
      }
    );
  };

  const _onCloseConfirmModal = () => {
    setConfirmModalConfig({...INITIAL_MODAL_CONFIG});
  }

  const _clearStateAfterDownload = () => {
    _onCloseConfirmModal();
    setFile(null);
    setData([]);
    setHeaders([]);
    setError("");
    setEventId("");
    setAction("create");
    setPageNumber(1);
    setExportId(null);
  }
  
  const eventIds = (eventsData?.data ?? []).map((event: IEventIdsItem) => (
    { value: event.id, label: event.name_vi }
  ));

  return (
    <>
      <div className="box-container">
        <div className="flex flex-col gap-y-4 md:flex-row md:items-end md:gap-x-3">
          <div className="flex-1">
            <label htmlFor="event_id" className="block text-sm font-medium leading-6">
              Event Name
            </label>
            <div className="mt-2">
              <Select
                key={`eventId__${eventId}`}
                options={eventIds}
                id="event_id"
                isLoading={isFetching}
                placeholder="Select Event"
                className="py-0.5"
                value={eventIds.find((d) => d.value === eventId)}
                onChange={(selected: {label: string; value: string}) => setEventId(selected?.value)}
              />
            </div>
          </div>
          <div className="flex-1">
            <label htmlFor="action" className="block text-sm font-medium leading-6">
              Action
            </label>
            <div className="mt-2">
              <Select
                key={`action__${action}`}
                options={IMPORT_ACTIONS}
                id="action"
                placeholder="Select Action"
                className="py-0.5"
                value={IMPORT_ACTIONS.find((d) => d.value === action)}
                onChange={(selected: {label: string; value: string}) => setAction(selected?.value)}
              />
            </div>
          </div>
          <div className="shrink-0">
            <label htmlFor="import" className="block text-sm font-medium leading-6">
              <span className="sr-only">Import</span>
            </label>
            <div className="mt-2 w-full">
              <Button 
                onClick={handleFileSelect}
                className="w-full bg-white rounded-md px-3 text-brand-500 py-2 border-solid border-brand-500 border-[1px] hover:bg-white active:bg-white active:opacity-85"
              >
                Import
              </Button>
            </div>
          </div>
        </div>
      </div>
      <div className="box-container">
        {data && isArray(data) ? (
          <p className="text-sm mb-2">
            Total rows:  <span className="font-medium">{data?.length}</span>
          </p>
        ) : <></>}
        {error && (
          <p className="error-text">
            <span className="font-medium">
              {error}
            </span>
          </p>
        )}
        {data.length === 0 && !error && (
          <p className="text-center text-sm">
            No Data
          </p>
        )}
        <div className="sm:hidden">
          <MassPasscodeCard
            data={paginate(data, pageNumber, DEFAULT_PAGE_SIZE)}
          />
        </div>
        <div className="hidden sm:block">
          <MassPasscodeTable 
            data={paginate(data, pageNumber, DEFAULT_PAGE_SIZE)}
            headers={headers}
          />
        </div>
        {data.length >= DEFAULT_PAGE_SIZE + 1 ? (
          <div className="mt-6">
            <Pagination
              hideTotal
              currentPage={pageNumber}
              totalPage={Math.ceil(data.length/DEFAULT_PAGE_SIZE)}
              total={{
                totalItems: data.length ?? 0,
                total: data.length ?? 0,
              }}
              onChange={(page) => setPageNumber(page)}
            />
          </div>
        ) : <></>}
      </div>
      {(!isEmpty(data) && !isEmpty(eventId) && !isEmpty(action)) ? (
        <div className="w-full px-4 py-4 sm:px-6 bg-white sticky bottom-0 z-[2] mt-auto">
          <div className="flex items-center justify-end gap-x-4">
            <Link to={"/events/passcodes"}>
              <Button className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
                Cancel
              </Button>
            </Link>
            <Button className="w-20" onClick={_submit}>
              Save
            </Button>
          </div>
        </div>
      ) : <></>}
      
      {(exportId || isLoading) ? <FullScreenLoading /> : <></>}
      <ConfirmModal 
        key="import-result"
        title="Notification"
        content=""
        customContent={(
          <div className='mt-4 text-center'>
            <p className='text-sm'>Some data rows could not be imported. Please download the file to view the details.</p>
          </div>
        )}
        hideCancelButton={true}
        closeOnClickBackdrop={false}
        open={confirmModalConfig.open}
        onConfirm={confirmModalConfig.submit}
        onClose={_onCloseConfirmModal}
        customSubmitButton={(
          <Button
            className="bg-white border-brand-500 border-[1px] border-solid text-brand-500 hover:bg-white active:opacity-85 active:bg-transparent w-full"
            onClick={confirmModalConfig.submit}
          >
            <span className="sr-only">Download result file</span>
            <DownloadIcon className="w-4 h-4" />{" "}
            <span className="ml-2">Download result file</span>
          </Button>
        )}
      />
    </>
  );
};
